import { useEffect, useState, useMemo } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { lazyAuth } from "@/lib/supabase-lazy";
import { sendWelcomeEmail } from "@/lib/email.functions";

function PostLoginButton() {
  const search = useSearch({ from: "/bekraftad" });
  const redirect =
    typeof search === "object" && search !== null && "redirect" in search
      ? String((search as Record<string, unknown>).redirect)
      : "";
  const isLogin =
    typeof search === "object" && search !== null && "login" in search
      ? String((search as Record<string, unknown>).login) === "1"
      : false;
  const safeRedirect = useMemo(() => {
    if (!redirect || !redirect.startsWith("/")) return isLogin ? "/oversikt" : "/skapa";
    // Tillåt bara interna sökvägar inom samma ursprung.
    try {
      const u = new URL(redirect, window.location.origin);
      if (u.origin !== window.location.origin) return "/oversikt";
      return u.pathname + u.search;
    } catch {
      return "/oversikt";
    }
  }, [redirect, isLogin]);

  return (
    <Button asChild className="mt-6">
      <Link to={safeRedirect} resetScroll={true}>
        {isLogin ? "Fortsätt" : "Tillbaka till min annons"}
      </Link>
    </Button>
  );
}

export const Route = createFileRoute("/bekraftad")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "E-postadressen är bekräftad — Sellavo" },
      {
        name: "description",
        content:
          "Ditt Sellavo-konto är aktiverat. Gå tillbaka till fliken där du skapade din annons och fortsätt publicera.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "E-postadressen är bekräftad — Sellavo" },
      {
        property: "og:description",
        content: "Ditt Sellavo-konto är aktiverat och du kan fortsätta publicera din annons.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ConfirmedPage,
});

function ConfirmedPage() {
  const [ready, setReady] = useState(false);
  const [otherTab, setOtherTab] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    setIsLogin(new URLSearchParams(window.location.search).get("login") === "1");
  }, []);

  useEffect(() => {
    let cancelled = false;
    let finished = false;

    const params = new URLSearchParams(window.location.search);
    const draftId = params.get("utkast") ?? "";
    const loginOnly = params.get("login") === "1";
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

    let currentSession: Awaited<ReturnType<typeof lazyAuth.getSession>>["data"]["session"] =
      null;

    let listener: BroadcastChannel | null = null;
    try {
      listener = new BroadcastChannel("sellavo:auth");
      listener.onmessage = (event: MessageEvent<{ type?: string }>) => {
        if (event.data?.type === "resumed") setOtherTab(true);
      };
    } catch {
      // ignore
    }

    const ping = () => {
      if (!currentSession) return;
      const email = currentSession.user?.email ?? "";
      try {
        const channel = new BroadcastChannel("sellavo:auth");
        channel.postMessage({ type: "signed-in", draftId, email, session: currentSession });
        channel.close();
      } catch {
        // ignore
      }
      try {
        window.localStorage.setItem(
          "sellavo:auth-ping",
          JSON.stringify({ at: Date.now(), draftId, email }),
        );
      } catch {
        // ignore
      }
    };

    const finish = (session: NonNullable<typeof currentSession>) => {
      if (cancelled || finished) return;
      finished = true;
      currentSession = session;
      setReady(true);
      ping();
      // Inloggning med länk är inget nytt konto – då skickas inget välkomstmejl.
      if (!loginOnly) void sendWelcomeEmail({ data: undefined }).catch(() => undefined);
    };

    // Länken kan innehålla ett felmeddelande (redan använd eller utgången).
    // Då får vi absolut inte visa "Du är klar!" bara för att en gammal
    // session ligger kvar i webbläsaren.
    const linkErrorCode = hash.get("error") ?? params.get("error");
    if (linkErrorCode) {
      setLinkError(
        "Länken har redan använts eller gått ut. Gå tillbaka till fliken där du skapade kontot och skicka en ny kod.",
      );
      return () => {
        cancelled = true;
        listener?.close();
      };
    }

    // Bär länken med sig färska tokens är det de – och bara de – som gäller.
    // Annars kunde en gammal inloggning i samma webbläsare tas för den nya.
    const accessToken = hash.get("access_token");
    const refreshToken = hash.get("refresh_token");

    const applyLinkTokens = async () => {
      if (!accessToken || !refreshToken) return false;
      const { data, error } = await lazyAuth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      if (error || !data.session) {
        setLinkError(
          "Länken kunde inte användas. Gå tillbaka till fliken där du skapade kontot och skicka en ny kod.",
        );
        return true;
      }
      finish(data.session);
      return true;
    };

    const { data: sub } = lazyAuth.onAuthStateChange((_e, session) => {
      // Bara sessioner som hör till länken får räknas när länken bär tokens.
      if (session && !(accessToken && !finished)) finish(session);
    });

    const checkSession = async () => {
      const { data } = await lazyAuth.getSession();
      if (data.session) finish(data.session);
    };

    let poll = 0;
    let repeat = 0;
    void (async () => {
      const handled = await applyLinkTokens();
      if (cancelled) return;
      if (handled) {
        repeat = window.setInterval(ping, 1500);
        return;
      }
      void checkSession();
      poll = window.setInterval(() => void checkSession(), 500);
      // Extra pingar ifall den andra fliken hann vakna först
      repeat = window.setInterval(ping, 1500);
    })();

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      window.clearInterval(repeat);
      sub.subscription.unsubscribe();
      listener?.close();
    };
  }, []);

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
        {linkError ? (
          <AlertCircle className="h-7 w-7 text-destructive" />
        ) : ready ? (
          <CheckCircle2 className="h-7 w-7" />
        ) : (
          <Loader2 className="h-7 w-7 animate-spin" />
        )}
      </div>
      <h1 className="mt-6 font-heading text-2xl font-semibold text-foreground sm:text-3xl">
        {linkError
          ? "Länken gick inte att använda"
          : ready
            ? isLogin
              ? "Du är inloggad!"
              : "Du är klar!"
            : isLogin
              ? "Loggar in…"
              : "Bekräftar…"}
      </h1>
      <p className="mt-3 text-muted-foreground">
        {linkError
          ? linkError
          : !ready
            ? "Ett ögonblick."
            : otherTab
              ? "Din annons fortsätter i fliken där du började — gå tillbaka dit. Den här fliken kan du stänga."
              : isLogin
                ? "Välkommen tillbaka till Sellavo. Nu kan du publicera, redigera och svara på meddelanden."
                : "Grattis till ditt nya konto! Fortsätt med annonsen här."}
      </p>
      {ready && !linkError && !otherTab && <PostLoginButton />}
    </main>
  );
}
